/**
 * Joycelyn Fashion — Interactive Catalog Toolbar Controller
 * Controls: Grid Switcher Modes, Live Mini-Search Filtering & Instant Card Sorting
 */
(function() {
  const STORAGE_KEY = 'joycelyn_preferred_grid_view';

  function initToolbar() {
    initGridSwitcher();
    initMiniSearch();
    initCatalogSort();
  }

  /* -------------------------------------------------------------------------
     1. GRID MODE SWITCHER (3-Cols, 2-Cols, Editorial Masonry)
  ------------------------------------------------------------------------- */
  function initGridSwitcher() {
    const switchers = document.querySelectorAll('.grid-switcher-toolbar');
    if (!switchers.length) return;

    const savedMode = localStorage.getItem(STORAGE_KEY) || 'classic-3';
    applyGridMode(savedMode);

    switchers.forEach(toolbar => {
      const buttons = toolbar.querySelectorAll('[data-grid-mode]');
      buttons.forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          const targetMode = btn.getAttribute('data-grid-mode');
          applyGridMode(targetMode);
          try {
            localStorage.setItem(STORAGE_KEY, targetMode);
          } catch(err) {}
        });
      });
    });
  }

  function applyGridMode(mode) {
    const validModes = ['masonry', 'classic-3', 'classic-2'];
    const currentMode = validModes.includes(mode) ? mode : 'classic-3';

    const grids = document.querySelectorAll('.joycelyn-product-grid, .masonry-product-grid, #ProductGridContainer > div, #JoycelynProductGrid');
    grids.forEach(grid => {
      validModes.forEach(m => {
        grid.classList.remove('is-mode-' + m);
      });
      grid.classList.add('is-mode-' + currentMode);
      grid.classList.add('joycelyn-product-grid');

      if (currentMode === 'masonry') {
        const cards = grid.querySelectorAll('.product-card-masonry');
        cards.forEach((card, index) => {
          card.classList.remove('is-hero-card', 'is-tall', 'is-standard');
          const mod = index % 7;
          if (mod === 0) {
            card.classList.add('is-hero-card');
          } else if (mod === 2 || mod === 5) {
            card.classList.add('is-tall');
          } else {
            card.classList.add('is-standard');
          }
        });
      }
    });

    // Update active styles on buttons
    document.querySelectorAll('[data-grid-mode]').forEach(btn => {
      const btnMode = btn.getAttribute('data-grid-mode');
      if (btnMode === currentMode) {
        btn.classList.add('is-active');
        btn.style.background = '#924BC9';
        btn.style.color = '#FFFFFF';
        btn.style.boxShadow = '0 0 0 1px rgba(197, 160, 89, 0.45), 0 4px 14px rgba(146, 75, 201, 0.4)';
      } else {
        btn.classList.remove('is-active');
        btn.style.background = 'transparent';
        btn.style.color = 'var(--color-text-muted)';
        btn.style.boxShadow = 'none';
      }
    });
  }

  /* -------------------------------------------------------------------------
     2. MINI SEARCH LIVE FILTERING
  ------------------------------------------------------------------------- */
  function initMiniSearch() {
    const searchInputs = document.querySelectorAll('.js-mini-search');
    if (!searchInputs.length) return;

    searchInputs.forEach(input => {
      const parentToolbar = input.closest('.catalog-toolbar-capsule');
      const clearBtn = parentToolbar ? parentToolbar.querySelector('.js-mini-search-clear') : null;
      const countEl = parentToolbar ? parentToolbar.querySelector('.js-toolbar-count') : null;

      // Find the associated product grid container
      const container = parentToolbar.closest('.couture-container') || document;
      const cards = container.querySelectorAll('.product-card-masonry');
      const initialTotal = cards.length;

      input.addEventListener('input', function() {
        const query = input.value.trim().toLowerCase();
        let visibleCount = 0;

        if (clearBtn) {
          clearBtn.style.display = query.length > 0 ? 'inline-block' : 'none';
        }

        cards.forEach(card => {
          if (!query) {
            // Restore visibility (respecting loadmore if present)
            if (!card.classList.contains('is-loadmore-hidden')) {
              card.style.display = '';
            }
            visibleCount++;
            return;
          }

          const title = (card.getAttribute('data-product-title') || '').toLowerCase();
          const tags = (card.getAttribute('data-product-tags') || '').toLowerCase();
          const type = (card.getAttribute('data-product-type') || '').toLowerCase();
          const vendor = (card.getAttribute('data-product-vendor') || '').toLowerCase();
          const cardText = card.textContent.toLowerCase();

          const matches = title.includes(query) || tags.includes(query) || type.includes(query) || vendor.includes(query) || cardText.includes(query);

          if (matches) {
            card.style.display = '';
            visibleCount++;
          } else {
            card.style.display = 'none';
          }
        });

        if (countEl) {
          countEl.textContent = query ? visibleCount : initialTotal;
        }
      });

      if (clearBtn) {
        clearBtn.addEventListener('click', function(e) {
          e.preventDefault();
          input.value = '';
          clearBtn.style.display = 'none';
          input.dispatchEvent(new Event('input'));
          input.focus();
        });
      }
    });
  }

  /* -------------------------------------------------------------------------
     3. CLIENT-SIDE INSTANT CATALOG SORTING
  ------------------------------------------------------------------------- */
  function initCatalogSort() {
    const sortSelects = document.querySelectorAll('.js-catalog-sort');
    if (!sortSelects.length) return;

    sortSelects.forEach(select => {
      const parentToolbar = select.closest('.catalog-toolbar-capsule');
      const container = parentToolbar.closest('.couture-container') || document;
      const grid = container.querySelector('.joycelyn-product-grid, #JoycelynProductGrid');
      if (!grid) return;

      // Assign initial original index order
      const cards = Array.from(grid.querySelectorAll('.product-card-masonry'));
      cards.forEach((card, idx) => {
        if (!card.hasAttribute('data-original-index')) {
          card.setAttribute('data-original-index', idx);
        }
      });

      select.addEventListener('change', function() {
        const mode = select.value;
        const currentCards = Array.from(grid.querySelectorAll('.product-card-masonry'));

        currentCards.sort((a, b) => {
          if (mode === 'price-low-high') {
            const pA = parseFloat(a.getAttribute('data-product-price') || 0);
            const pB = parseFloat(b.getAttribute('data-product-price') || 0);
            return pA - pB;
          }
          if (mode === 'price-high-low') {
            const pA = parseFloat(a.getAttribute('data-product-price') || 0);
            const pB = parseFloat(b.getAttribute('data-product-price') || 0);
            return pB - pA;
          }
          if (mode === 'title-az') {
            const tA = (a.getAttribute('data-product-title') || '').toLowerCase();
            const tB = (b.getAttribute('data-product-title') || '').toLowerCase();
            return tA.localeCompare(tB);
          }
          if (mode === 'title-za') {
            const tA = (a.getAttribute('data-product-title') || '').toLowerCase();
            const tB = (b.getAttribute('data-product-title') || '').toLowerCase();
            return tB.localeCompare(tA);
          }
          if (mode === 'newest') {
            const dA = parseFloat(a.getAttribute('data-product-date') || 0);
            const dB = parseFloat(b.getAttribute('data-product-date') || 0);
            return dB - dA;
          }
          // Default: featured
          const oA = parseInt(a.getAttribute('data-original-index') || 0, 10);
          const oB = parseInt(b.getAttribute('data-original-index') || 0, 10);
          return oA - oB;
        });

        // Re-append in new order
        currentCards.forEach(c => grid.appendChild(c));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initToolbar);
  } else {
    initToolbar();
  }
})();
